/**
 * Active Crawl panel — top-of-page indicator on SearchPage.
 *
 * Visibility: rendered only when the tenant has ≥1 enabled schedule.
 * Otherwise renders nothing, keeping the page clean for one-shot users.
 *
 * Two layouts:
 *   - Single schedule  → full-detail card with name, next fire, last-run
 *                        summary, manage/pause/delete actions.
 *   - 2+ schedules     → compact list, one row per schedule + a
 *                        "Manage all schedules ↗" link.
 *
 * Capacity meter sits below either layout and shows the tenant's
 * estimated tokens/hr vs Keepa's 60 tok/hr ceiling. Tone shifts as the
 * percentage climbs: accent → warning at 60% → negative at 85%.
 *
 * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §1
 * and docs/plans/2026-05-23-searchpage-active-crawl-implementation.md
 * Task 6.
 */
import { Link } from '@tanstack/react-router';
import type { ActiveScheduleEntry, ActiveSchedulesResponse } from '@sourcito/schemas';
import { Card } from '../design';
import { useActiveSchedules } from '../lib/activeSchedules';

/**
 * Connected component used by SearchPage. Pulls data from the hook and
 * renders the pure presentation component (or null when there's nothing
 * to show).
 */
export function ActiveCrawlPanel() {
  const { data } = useActiveSchedules();
  if (!data || data.schedules.length === 0) return null;
  return <ActiveCrawlPanelView schedules={data.schedules} capacity={data.capacity} />;
}

interface Props {
  schedules: ActiveSchedulesResponse['schedules'];
  capacity: ActiveSchedulesResponse['capacity'];
}

/**
 * Pure presentation. Split out so it can be unit-tested in isolation
 * if/when vitest+jsdom land in apps/web. Today the page-level smoke test
 * is the verification surface — see implementation plan Task 7.
 */
export function ActiveCrawlPanelView({ schedules, capacity }: Props) {
  if (schedules.length === 0) return null;
  return (
    <Card className="border-accent/30 bg-accent/[0.04] mb-3 p-4 space-y-3">
      {schedules.length === 1 ? (
        <SingleScheduleRow schedule={schedules[0]!} />
      ) : (
        <MultiScheduleList schedules={schedules} />
      )}
      <CapacityMeter capacity={capacity} />
    </Card>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function SingleScheduleRow({ schedule }: { schedule: ActiveScheduleEntry }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-fg-tertiary flex items-center gap-1.5">
        <StatusDot status={schedule.status} />
        ACTIVE CRAWL
      </div>
      <div className="text-base font-medium leading-tight">
        {schedule.templateName}
        <span className="text-fg-tertiary mx-1.5">·</span>
        <span className="text-fg-secondary">{cronLabel(schedule.cronExpression)}</span>
      </div>
      <div className="text-sm text-fg-secondary">
        Next fire: {formatNextFire(schedule.nextFireAt)}
        {schedule.lastRun && (
          <>
            <span className="text-fg-tertiary mx-1.5">·</span>
            Last run: {schedule.lastRun.uniqueBrandCount} brands enqueued,{' '}
            {schedule.lastRun.brandsEnriched} fresh
          </>
        )}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Link
          to="/schedules"
          className="text-xs text-accent-fg hover:underline transition-colors duration-fast"
        >
          Manage schedule ↗
        </Link>
      </div>
    </div>
  );
}

function MultiScheduleList({ schedules }: { schedules: ActiveScheduleEntry[] }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">ACTIVE CRAWLS</div>
      <div className="divide-y divide-border-subtle/60">
        {schedules.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
            <span className="flex items-center gap-2 text-sm">
              <StatusDot status={s.status} />
              <span className="font-medium">{s.templateName}</span>
              <span className="text-fg-tertiary">{cronLabel(s.cronExpression)}</span>
            </span>
            <span className="text-xs text-fg-tertiary">{formatNextFire(s.nextFireAt)}</span>
          </div>
        ))}
      </div>
      <Link
        to="/schedules"
        className="text-xs text-accent-fg hover:underline transition-colors duration-fast"
      >
        Manage all schedules ↗
      </Link>
    </div>
  );
}

function CapacityMeter({ capacity }: { capacity: ActiveSchedulesResponse['capacity'] }) {
  // Tone shifts as we get closer to the ceiling. The numbers come from
  // §4 of the design doc: ad-hoc + brand enrichment combined eat ~25
  // tok/hr in steady state, so >60% used is the "watch out" zone and
  // >85% is "running into the wall."
  const tone =
    capacity.percentUsed >= 85
      ? 'bg-negative'
      : capacity.percentUsed >= 60
        ? 'bg-warning'
        : 'bg-accent';
  const clamped = Math.min(100, capacity.percentUsed);
  return (
    <div className="text-xs text-fg-tertiary space-y-1.5 pt-1 border-t border-border-subtle/60">
      <div>
        Capacity used: <span className="text-fg-secondary">~{capacity.percentUsed}%</span> of Keepa
        refill (~{capacity.estimatedTokensPerHour} of {capacity.ceilingTokensPerHour} tok/hr)
      </div>
      <div
        className="h-1.5 w-full bg-surface-elev rounded-full overflow-hidden"
        aria-label={`Capacity used: ${capacity.percentUsed}%`}
        role="progressbar"
        aria-valuenow={capacity.percentUsed}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full ${tone} transition-[width] duration-slow ease-in-out-cubic`}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: ActiveScheduleEntry['status'] }) {
  // Visual shorthand. Keep the dot the only signal that varies by status —
  // shape/color, not new words — so the panel stays scannable.
  switch (status) {
    case 'running':
      return (
        <span className="text-positive" aria-label="Running">
          ●
        </span>
      );
    case 'paused':
      return (
        <span className="text-warning" aria-label="Paused">
          ⏸
        </span>
      );
    case 'compliance_paused':
      return (
        <span className="text-negative" aria-label="Paused by compliance">
          ⚠
        </span>
      );
    case 'budget_waiting':
      return (
        <span className="text-warning" aria-label="Waiting for budget">
          ⏳
        </span>
      );
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function cronLabel(cron: string): string {
  if (cron === '0 * * * *') return 'hourly';
  if (/^0 \d{1,2} \* \* \*$/.test(cron)) return 'daily';
  if (/^0 0 \* \* 0$/.test(cron)) return 'weekly';
  return cron;
}

function formatNextFire(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return 'overdue';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `in ${min} min`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}
